import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Clock, MessageSquare, UserCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useTicketAdmin } from '@/hooks/useTicketAdmin';
import {
  ADMIN_TICKET_STATUSES,
  HIGH_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  ticketCategoryLabel,
  ticketSourceLabel,
  ticketStatusConfig,
} from '@/lib/ticketStatus';

interface AdminTicket {
  id: string;
  ticket_number: string;
  subject: string;
  description: string | null;
  status: string;
  priority: string;
  category: string;
  created_at: string;
  reporter_name: string | null;
  assigned_to: string | null;
  metadata: Record<string, unknown> | null;
}

const PAGE_SIZE = 30;
const POLLING_INTERVAL = 120_000;

/**
 * AdminTicketsPage — ärendehantering för ärendeadmins (superadmin samt
 * org-admins: customer_admin/partner_admin/hq_admin — samma roller som
 * RLS-helpern has_admin_role godkänner).
 *
 * Samma struktur som ärendelistan i pwa-handel (Card-lista i en max-w-md-
 * kolumn med statusfilter och paginering), anpassad till åtkomstappens
 * Header. Ärendena läses ur den delade tickets-tabellen så ärenden från
 * alla appar landar här (källan visas som badge). Rollchecken är enbart
 * UX; RLS på tickets (is_superadmin/has_admin_role) är den verkliga
 * behörighetsgränsen, och customer_admin-frågor filtreras dessutom
 * explicit på organization_id (defense-in-depth).
 */
export default function AdminTicketsPage() {
  const navigate = useNavigate();
  const { isTicketAdmin, isSuperadmin, organizationId, isLoading: roleLoading } = useTicketAdmin();

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  // Icke-admins skickas hem (UX-gate — RLS skyddar datat oavsett).
  useEffect(() => {
    if (!roleLoading && !isTicketAdmin) navigate('/', { replace: true });
  }, [roleLoading, isTicketAdmin, navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-tickets', isSuperadmin, organizationId, statusFilter, page],
    queryFn: async () => {
      let query = supabase
        .from('tickets')
        .select(
          'id, ticket_number, subject, description, status, priority, category, created_at, reporter_name, assigned_to, metadata',
          { count: 'exact' }
        )
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      // Defense-in-depth: RLS begränsar redan, men customer_admin filtreras
      // även explicit på sin organisation.
      if (!isSuperadmin && organizationId) {
        query = query.eq('organization_id', organizationId);
      }
      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { tickets: (data ?? []) as AdminTicket[], total: count ?? 0 };
    },
    enabled: isTicketAdmin,
    refetchInterval: POLLING_INTERVAL,
    staleTime: 30_000,
  });

  const selectStatus = (status: string | null) => {
    setStatusFilter(status);
    setPage(0);
  };

  if (roleLoading || !isTicketAdmin) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </main>
      </div>
    );
  }

  const tickets = data?.tickets ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const filterChips: Array<{ label: string; value: string | null }> = [
    { label: 'Alla', value: null },
    ...ADMIN_TICKET_STATUSES.map((s) => ({ label: s.label, value: s.value as string | null })),
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto w-full max-w-md flex-1 space-y-4 px-4 py-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">Ärenden</h1>
          <span className="text-xs text-muted-foreground">
            {total} {total === 1 ? 'ärende' : 'ärenden'}
          </span>
        </div>

        {/* Statusfilter */}
        <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-4 px-4 py-1">
          {filterChips.map(({ label, value }) => {
            const isActive = statusFilter === value;
            return (
              <button
                key={label}
                onClick={() => selectStatus(value)}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card/50 text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : tickets.length > 0 ? (
          <div className="space-y-3">
            {tickets.map((ticket) => {
              const statusCfg = ticketStatusConfig(ticket.status);
              const source = ticketSourceLabel(ticket.metadata);
              return (
                <Card
                  key={ticket.id}
                  className="cursor-pointer border-border bg-card/50 p-4 backdrop-blur-sm transition-colors hover:bg-card/70"
                  onClick={() => navigate(`/admin/arenden/${ticket.id}`)}
                >
                  <div className="space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="truncate font-medium text-foreground">{ticket.subject}</h3>
                        <p className="text-xs text-muted-foreground">
                          {ticket.reporter_name ? `${ticket.reporter_name} · ` : ''}
                          {ticketCategoryLabel(ticket.category)}
                        </p>
                      </div>
                      <Badge className={`shrink-0 ${statusCfg.badgeClass}`}>
                        {statusCfg.label}
                      </Badge>
                    </div>

                    {ticket.description && (
                      <p className="line-clamp-2 text-sm text-muted-foreground">
                        {ticket.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-muted-foreground">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-[11px]">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(ticket.created_at), {
                            addSuffix: true,
                            locale: sv,
                          })}
                        </span>
                        <span className="text-[11px]">#{ticket.ticket_number}</span>
                        {HIGH_PRIORITIES.includes(ticket.priority) && (
                          <span className="text-[11px] font-medium text-destructive">
                            {TICKET_PRIORITY_LABELS[ticket.priority] ?? ticket.priority}
                          </span>
                        )}
                      </div>
                      <span className="flex items-center gap-1.5">
                        {source && (
                          <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                            {source}
                          </Badge>
                        )}
                        {ticket.assigned_to ? (
                          <UserCheck className="h-4 w-4" />
                        ) : (
                          <MessageSquare className="h-4 w-4" />
                        )}
                      </span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-border bg-card/50 p-8 text-center backdrop-blur-sm">
            <MessageSquare className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 font-medium text-foreground">Inga ärenden</h3>
            <p className="text-sm text-muted-foreground">
              {statusFilter
                ? 'Inga ärenden med den valda statusen'
                : 'Kunders supportärenden visas här'}
            </p>
          </Card>
        )}

        {/* Paginering */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Föregående
            </Button>
            <span className="text-[11px] text-muted-foreground">
              Sida {page + 1} av {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="gap-1"
            >
              Nästa
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
